import { unwrapResult } from '@reduxjs/toolkit'
import { Formik } from 'formik'
import React, { useEffect } from 'react'
import * as Yup from 'yup'

import { FormActions } from 'src/modules/auth'
import { useActionRunner } from 'src/modules/notification'
import {
  Button,
  Container,
  Form,
  FormikTextField,
  PageTitle,
  FIELD_REQUIRED,
} from 'src/modules/ui'

import { useAdmin } from '../hooks'

const BusinessSettingsForm = ({ handleSubmit, isSubmitting }) => {
  return (
    <Container>
      <PageTitle>Business Settings</PageTitle>
      <Form>
        <Form.InputGroup>
          <FormikTextField
            fullWidth
            label="Business Name"
            margin="normal"
            name="name"
            type="text"
          />
        </Form.InputGroup>
        <Form.InputGroup title="Registered Address">
          <FormikTextField
            fullWidth
            label="Address line 1"
            margin="normal"
            name="registeredAddress.line1"
            type="text"
          />
          <FormikTextField
            fullWidth
            label="Address line 2"
            margin="normal"
            name="registeredAddress.line2"
            type="text"
          />
          <FormikTextField
            fullWidth
            label="City"
            margin="normal"
            name="registeredAddress.city"
            type="text"
          />
          <FormikTextField
            fullWidth
            label="County"
            margin="normal"
            name="registeredAddress.county"
            type="text"
          />
          <FormikTextField
            fullWidth
            label="Postcode"
            margin="normal"
            name="registeredAddress.postcode"
            type="text"
          />
        </Form.InputGroup>
        <Form.InputGroup title="Primary Contact">
          <FormikTextField
            fullWidth
            label="Primary Contact - Name"
            margin="normal"
            name="primaryContactName"
            type="text"
          />
          <FormikTextField
            fullWidth
            label="Primary Contact - Email"
            margin="normal"
            name="primaryContactEmail"
            type="email"
          />
        </Form.InputGroup>
        <FormActions>
          <Button
            color="primary"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            size="large"
            type="submit"
          >
            Submit
          </Button>
        </FormActions>
      </Form>
    </Container>
  )
}

const BusinessSettings = () => {
  const { fetchBusinessSettings, updateBusinessSettings, businessSettings } =
    useAdmin()

  const [fetchData] = useActionRunner(fetchBusinessSettings)
  const [patchData] = useActionRunner(updateBusinessSettings, {
    successNotification: 'Settings Updated!',
  })
  const initialValues = businessSettings

  useEffect(() => {
    fetchData()
  }, [])

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(FIELD_REQUIRED),
    primaryContactName: Yup.string().required(FIELD_REQUIRED),
    primaryContactEmail: Yup.string().email().required(FIELD_REQUIRED),
    registeredAddress: Yup.object().shape({
      name: Yup.string().required(FIELD_REQUIRED),
      line1: Yup.string().required(FIELD_REQUIRED),
      line2: Yup.string().notRequired().nullable(),
      city: Yup.string().required(FIELD_REQUIRED),
      county: Yup.string().required(FIELD_REQUIRED),
      postcode: Yup.string().required(FIELD_REQUIRED),
    }),
  })

  const handleSubmit = async (values, context) => {
    const { setErrors } = context
    try {
      const result = await patchData(values)
      unwrapResult(result)
    } catch (error) {
      setErrors(error.data)
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {formik => <BusinessSettingsForm {...formik} />}
    </Formik>
  )
}

export default BusinessSettings
